// DeleteAccountDialog.tsx — permanent account deletion (Apple 5.1.1 + GDPR erasure).
// Deliberately hard to trigger by accident: the user must TYPE "DELETE ME". On
// success it purges server data + deletes the Supabase auth user, then resets local
// progress and signs out. Billing is NOT auto-cancelled — we tell them to cancel any
// subscription with Stripe first. House style: no emoji, no em-dash.
import React from 'react';
import { Display, Body, Btn } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useProgress } from '@/hooks/useProgress';
import { deleteAccount, clearSyncState, openBillingPortal } from '@/services/sync';
import { SHOW_PURCHASE, SHOW_IAP } from '@/config/supabase';
import { Browser } from '@capacitor/browser';
import { manageSubscriptionUrl } from '@/services/purchases';

const CONFIRM = 'DELETE ME';

export function DeleteAccountDialog({ onClose }: { onClose: () => void }) {
  const auth = useAuth();
  const progress = useProgress();
  const storeSource: 'apple' | 'google' | null =
    auth.entitlementSource === 'apple' || auth.entitlementSource === 'google' ? auth.entitlementSource : null;
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const ready = text.trim().toUpperCase() === CONFIRM;

  // Only paying members have a Stripe subscription to cancel; show the link to the
  // (activated) Stripe billing/cancellation console for them. Deleting here does NOT
  // stop billing, so they must cancel the subscription with Stripe themselves.
  const token = auth.session?.accessToken;
  const canCancel = auth.isMember && !!token && token !== 'local';
  const [billingBusy, setBillingBusy] = React.useState(false);
  const [billingErr, setBillingErr] = React.useState<string | null>(null);
  const onCancelSubscription = async () => {
    if (!canCancel || billingBusy || !token) return;
    setBillingBusy(true); setBillingErr(null);
    const res = await openBillingPortal(token);
    setBillingBusy(false);
    if (!res.ok) setBillingErr(res.error || 'Could not open the Stripe cancellation page. Please try again.');
  };

  const onDelete = async () => {
    if (!ready || busy) return;
    const token = auth.session?.accessToken;
    if (!token || token === 'local') { setError('Please sign in again, then try.'); return; }
    setBusy(true); setError(null);
    // Pass current research consent so the backend keeps the consented (now
    // anonymous) research dataset and wipes everything else.
    const res = await deleteAccount(token, progress.state.researchConsent === 'granted');
    if (res.ok) {
      try { progress.reset(); } catch { /* best-effort local wipe */ }
      try { clearSyncState(); } catch { /* best-effort */ }
      auth.signOut();
      onClose();
    } else {
      setBusy(false);
      setError(res.error || 'Could not delete your account. Please try again.');
    }
  };

  const billingNote = storeSource
    ? <>Deleting your account does <b>not</b> cancel your {storeSource === 'apple' ? 'App Store' : 'Google Play'} subscription. To stop it renewing, cancel it in your {storeSource === 'apple' ? 'App Store' : 'Google Play'} subscription settings first.</>
    : SHOW_PURCHASE
    ? <>If you pay monthly, deleting your account does <b>not</b> cancel your billing. Cancel it first in the app, under Account, then Cancel subscription.</>
    : SHOW_IAP
    ? (auth.isMember ? <>Deleting your account does <b>not</b> cancel a membership paid for elsewhere. If you have one, cancel it where you set it up.</> : null)
    : <>If you have a paid membership, deleting your account here does <b>not</b> end it. Manage or cancel it on the ReNurture website: renurture.org/leave-renurture</>;

  return (
    <div role="dialog" aria-modal="true" aria-label="Delete account" style={overlay}>
      <div style={panel}>
        <Display text="Delete your account" size={26} weight={400} style={{ marginBottom: 12 }} />
        <Body size={14.5} style={{ marginBottom: 14, lineHeight: 1.6 }}>
          This permanently deletes your ReNurture account and your saved progress. It cannot be undone, and you will lose access to the programme.
        </Body>

        <div style={noteBox}>
          {billingNote && (
            <Body size={13} style={{ lineHeight: 1.55 }}>{billingNote}</Body>
          )}
          {/* Stripe cancellation is a billing surface — web only. Native apps carry no
              purchasing/billing UI (the line above tells the user where it is handled). */}
          {storeSource && (
            <Btn
              full
              variant="ghost"
              size="sm"
              onClick={() => void Browser.open({ url: manageSubscriptionUrl(storeSource) }).catch(() => undefined)}
              style={{ marginTop: 12 }}
            >
              Manage subscription
            </Btn>
          )}
          {SHOW_PURCHASE && !storeSource && canCancel && (
            <Btn
              full
              variant="ghost"
              size="sm"
              onClick={() => void onCancelSubscription()}
              disabled={billingBusy}
              style={{ marginTop: 12 }}
              ariaLabel="Open the Stripe page to cancel your subscription"
            >
              {billingBusy ? 'Opening Stripe…' : 'Cancel my subscription in Stripe'}
            </Btn>
          )}
          {SHOW_PURCHASE && billingErr && (
            <Body role="alert" size={12.5} style={{ marginTop: 8, color: '#8B2F1A', lineHeight: 1.5 }}>{billingErr}</Body>
          )}
          <Body size={12.5} style={{ marginTop: billingNote ? 10 : 0, lineHeight: 1.5, color: 'var(--ink-soft)' }}>
            Need a hand? Email <b>support@renurture.org</b> and we will help.
          </Body>
        </div>

        <Body size={13} style={{ margin: '18px 0 8px' }}>
          To confirm, type <b>{CONFIRM}</b> below.
        </Body>
        <input
          type="text"
          autoCapitalize="characters"
          autoComplete="off"
          autoCorrect="off"
          placeholder={CONFIRM}
          aria-label={'Type ' + CONFIRM + ' to confirm'}
          value={text}
          onChange={(e) => { setText(e.target.value); setError(null); }}
          style={field}
        />
        {error && (
          <Body role="alert" size={13} style={{ marginTop: 10, color: '#8B2F1A', lineHeight: 1.5 }}>{error}</Body>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <Btn full variant="ghost" size="lg" onClick={onClose} disabled={busy}>Keep my account</Btn>
          <Btn
            full
            size="lg"
            onClick={() => void onDelete()}
            disabled={!ready || busy}
            ariaLabel="Permanently delete my account"
            style={ready && !busy ? { background: '#8B2F1A', color: '#fff', borderColor: '#8B2F1A' } : {}}
          >
            {busy ? 'Deleting…' : 'Delete account'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1100,
  background: 'rgba(20,16,12,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  paddingTop: 'calc(var(--safe-top) + 24px)',
  paddingBottom: 'calc(var(--safe-bottom) + 24px)',
};
const panel: React.CSSProperties = {
  width: '100%',
  maxWidth: 440,
  background: 'var(--card-top, #fff)',
  border: '1px solid var(--hair-strong, rgba(0,0,0,0.1))',
  borderRadius: 20,
  padding: 24,
  textAlign: 'left',
  maxHeight: '90vh',
  overflowY: 'auto',
};
const noteBox: React.CSSProperties = {
  background: '#FFF7EC',
  border: '1px solid rgba(196,162,72,0.4)',
  borderRadius: 12,
  padding: '12px 14px',
};
const field: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '13px 14px',
  borderRadius: 13,
  border: '1px solid var(--hair-strong, rgba(0,0,0,0.18))',
  background: 'var(--cream, #F7F5F0)',
  color: 'var(--ink)',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 16,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  outline: 'none',
};
